import { AsyncPipe, TitleCasePipe } from '@angular/common';
import { Component, computed, input, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { DateFormatPipe } from 'ngx-moment';
import { ItemDetails } from 'src/app/models/item-details';
import { UserIdToNamePipe } from 'src/app/shared/pipes/user-id-to-name.pipe';
import { JobDescriptionPipe } from './job-description.pipe';
@Component({
  selector: 'app-transport-jobs',
  standalone: true,
  imports: [
    MatIconModule,
    MatButtonModule,
    JobDescriptionPipe,
    AsyncPipe,
    MatTooltipModule,
    RouterLink
  ],
  providers: [UserIdToNamePipe, DateFormatPipe, TitleCasePipe],
  template: `
    <div
      class="flex flex-col justify-start items-stretch gap-3 p-4 bg-white rounded-lg shadow-sm border border-slate-200"
    >
      <div
        class="flex flex-row justify-start items-center gap-x-3 pb-2 border-b border-slate-100"
      >
        <mat-icon class="text-blue-500">local_shipping</mat-icon>
        <h2 class="text-lg font-bold text-slate-800">Transport Jobs</h2>
      </div>
      @if (noUpcomingDeliveries()) {
        <div class="flex items-center justify-center py-6">
          <p class="text-slate-500 italic flex items-center gap-2">
            <mat-icon class="text-slate-400">info</mat-icon>
            No upcoming transport jobs
          </p>
        </div>
      } @else {
        <div
          class="flex flex-row items-center justify-between gap-2 p-2 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors max-w-screen-sm"
        >
          <p class="text-slate-700 basis-full max-w-screen-sm">
            {{ itemDetails().nextDelivery | jobDescription | async }}
          </p>
          <a
            [routerLink]="['/dashboard/my-delivery-jobs']"
            [queryParams]="{
              selectedId: itemDetails().nextDelivery.id
            }"
            mat-icon-button
            matTooltip="View job details"
            class="text-sm text-blue-600 hover:text-blue-800"
          >
            <mat-icon>link</mat-icon>
          </a>
        </div>
      }
      @if (otherDeliveries().length > 0) {
        <button
          mat-stroked-button
          color="primary"
          class="text-sm self-end hover:bg-blue-50 transition-colors"
          (click)="showOtherDeliveriesToggle.set(!showOtherDeliveriesToggle())"
        >
          <span class="flex items-center gap-1">
            {{
              showOtherDeliveriesToggle()
                ? 'Hide Other Deliveries'
                : 'Show ' +
                  otherDeliveries().length +
                  ' other pending deliveries'
            }}
            <mat-icon class="text-sm">{{
              showOtherDeliveriesToggle() ? 'expand_less' : 'expand_more'
            }}</mat-icon>
          </span>
        </button>
        @if (showOtherDeliveriesToggle()) {
          <div
            class="flex flex-col items-stretch justify-start gap-2 mt-1 border-t border-slate-100 pt-2"
          >
            @for (delivery of otherDeliveries(); track delivery.id) {
              <div
                class="flex flex-row items-center justify-between gap-2 p-2 rounded-md hover:bg-slate-50 transition-colors max-w-screen-sm"
              >
                <p class="text-slate-600 max-w-screen-sm">
                  {{ delivery | jobDescription | async }}
                </p>
                <a
                  [routerLink]="['/dashboard/my-delivery-jobs']"
                  [queryParams]="{
                    selectedId: delivery.id
                  }"
                  mat-icon-button
                  matTooltip="View job details"
                  class="text-sm text-slate-500 hover:text-blue-600"
                >
                  <mat-icon>link</mat-icon>
                </a>
              </div>
            }
          </div>
        }
      }
    </div>
  `
})
export class TransportJobsComponent {
  itemDetails = input.required<ItemDetails>();
  otherDeliveries = computed(() => {
    return this.itemDetails().outstandingDeliveries.filter(
      (delivery) => delivery.id !== this.itemDetails().nextDelivery?.id
    );
  });

  noUpcomingDeliveries = computed(() => {
    return this.itemDetails().nextDelivery === null;
  });

  showOtherDeliveriesToggle = signal(false);
}
